import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Stack,
    Typography,
    IconButton,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { differenceInYears } from 'date-fns';
import FormProvider, {
    RHFTextField,
    RHFUploadAvatar,
    RHFPhoneInput,
} from '../../components/minimal/hook-form/index.jsx';
import RHFBirthdayPicker from '../../components/minimal/hook-form/RHFBirthdayPicker.jsx';
import { useLocales } from '../../locales';
import { useSnackbar } from '../../components/minimal/snackbar/index.jsx';
import { useSettingsContext } from '../../components/minimal/settings/SettingsContext';
import * as API from '../../api';
import { S3Handler } from '../../utils/s3.js';
import { fData } from '../../utils/minimal/formatNumber.js';
import Iconify from '../../components/minimal/iconify';

const CompleteFamilyProfileDialog = ({
    open,
    onClose,
    onSuccess,
    member,
    handler,
    token,
    subscriptionTemplate,
    account,
    familyRequiredFields,
}) => {
    const { translate } = useLocales();
    const { enqueueSnackbar } = useSnackbar();
    const settings = useSettingsContext();
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isChild = member?.familyRole === 'child';
    const maxAge = subscriptionTemplate?.familyChildMaxAge;

    // Determine required fields from server config, with sensible defaults
    const roleKey = isChild ? 'child' : 'adult';
    const requiredFields = familyRequiredFields?.[roleKey] || (isChild ? ['fname', 'lname', 'birth_date'] : ['fname', 'lname', 'phone', 'birth_date']);
    const isFieldRequired = (field) => requiredFields.includes(field);

    const schema = Yup.object().shape({
        fname: isFieldRequired('fname')
            ? Yup.string().trim().required(translate('dashboard.events.required_field'))
            : Yup.string().trim(),
        lname: isFieldRequired('lname')
            ? Yup.string().trim().required(translate('dashboard.events.required_field'))
            : Yup.string().trim(),
        birth_date: isFieldRequired('birth_date')
            ? Yup.mixed()
                  .required(translate('dashboard.events.required_field'))
                  .test('max-age', translate('family.age_exceeds_limit', { maxAge }), (value) => {
                      if (!value || !maxAge) return true;
                      const age = differenceInYears(new Date(), new Date(value));
                      return age <= maxAge;
                  })
            : Yup.mixed().nullable(),
        phone: isFieldRequired('phone')
            ? Yup.string().trim().required(translate('dashboard.events.required_field'))
            : Yup.string().trim(),
        imageUrl: Yup.mixed().nullable(),
    });

    const existingEndUser = member?.endUser || {};

    const methods = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            fname: existingEndUser.fname || '',
            lname: existingEndUser.lname || '',
            birth_date: existingEndUser.birth_date || null,
            phone: existingEndUser.phone || '',
            imageUrl: existingEndUser.imageUrl || null,
        },
    });

    const { handleSubmit, setValue, watch, reset } = methods;

    useEffect(() => {
        if (open && member) {
            const eu = member?.endUser || {};
            // Parse memberName into fname/lname if endUser data is not available
            const nameParts = member?.memberName?.split(' ') || [];
            const fallbackFname = nameParts[0] || '';
            const fallbackLname = nameParts.slice(1).join(' ') || '';
            reset({
                fname: eu.fname || fallbackFname,
                lname: eu.lname || fallbackLname,
                birth_date: eu.birth_date || member?.birth_date || null,
                phone: eu.phone || member?.phone || '',
                imageUrl: eu.imageUrl || member?.imageUrl || null,
            });
        }
    }, [open, member, reset]);

    const handleDrop = async (acceptedFiles) => {
        setIsUploadingImage(true);
        const file = acceptedFiles[0];
        if (file) {
            const newFile = Object.assign(file, { preview: URL.createObjectURL(file) });
            newFile.shouldRenew = true;
            setValue('imageUrl', newFile, { shouldValidate: true });
            try {
                newFile.finalPath = await S3Handler.uploadFile(newFile, null, null, null, true, token);
                setValue('imageUrl', newFile, { shouldValidate: true });
            } catch (e) {
                console.error('Error uploading image:', e);
                enqueueSnackbar(translate('general_error'), { variant: 'error' });
            }
        }
        setIsUploadingImage(false);
    };

    const onSubmit = async (data) => {
        try {
            setIsSubmitting(true);

            const payload = {
                fname: data.fname,
                lname: data.lname,
                birth_date: data.birth_date || undefined,
                ...(isFieldRequired('phone') ? { phone: data.phone } : {}),
            };

            if (data.imageUrl?.finalPath) {
                payload.imageUrl = data.imageUrl.finalPath;
            } else if (typeof data.imageUrl === 'string' && data.imageUrl) {
                payload.imageUrl = data.imageUrl;
            }

            const marketplaceId = account?.marketPlace?._id;
            const sellerId = account?._id;
            await API.completeFamilyProfile({ marketplaceId, sellerId, id: member._id, token, data: payload });
            enqueueSnackbar(translate('snackbar.details_updated_successfully'));
            onSuccess && onSuccess();
            reset();
        } catch (error) {
            console.error('Error completing family profile:', error);
            enqueueSnackbar(translate('general_error'), { variant: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        reset();
        onClose && onClose();
    };

    const imageValue = watch('imageUrl');

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6">
                        {member?.isProfileComplete
                            ? translate('family.edit_member')
                            : translate('family.complete_profile')}
                    </Typography>
                    <IconButton onClick={handleClose}>
                        <Iconify width="24px" icon="eva:close-outline" />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent>
                <FormProvider methods={methods}>
                    <Stack spacing={3} sx={{ pt: 1 }}>
                        {/* Photo upload */}
                        <Stack alignItems="center" spacing={1}>
                            <RHFUploadAvatar
                                name="imageUrl"
                                maxSize={3145728}
                                onDrop={handleDrop}
                                helperText={
                                    <Stack alignItems="center" spacing={0.5}>
                                        <Typography
                                            variant="caption"
                                            sx={{ display: 'block', textAlign: 'center', color: 'text.secondary' }}
                                        >
                                            {translate('image_upload.allowed_formats')}
                                            <br />
                                            {translate('image_upload.max_size', { size: fData(3145728) })}
                                        </Typography>
                                        <Typography variant="caption" color="warning.main">
                                            {translate('family.photo_required')}
                                        </Typography>
                                    </Stack>
                                }
                            />
                        </Stack>

                        {/* Name fields */}
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={2}
                        >
                            <RHFTextField
                                name="fname"
                                label={translate('family.first_name')}
                                fullWidth
                                required={isFieldRequired('fname')}
                            />
                            <RHFTextField
                                name="lname"
                                label={translate('family.last_name')}
                                fullWidth
                                required={isFieldRequired('lname')}
                            />
                        </Stack>

                        {/* Date of birth */}
                        <RHFBirthdayPicker
                            name="birth_date"
                            label={translate('family.date_of_birth')}
                            required={isFieldRequired('birth_date')}
                        />

                        {/* Phone - only for adults */}
                        {/* Phone — only shown if in required fields for this role */}
                        {isFieldRequired('phone') && (
                            <RHFPhoneInput
                                name="phone"
                                label={translate('phone')}
                                required
                                forceCallingCode={true}
                                sx={{ direction: settings?.themeDirection }}
                            />
                        )}
                    </Stack>
                </FormProvider>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button variant="outlined" onClick={handleClose}>
                    {translate('cancel')}
                </Button>
                <LoadingButton
                    variant="contained"
                    loading={isSubmitting || isUploadingImage}
                    onClick={handleSubmit(onSubmit)}
                >
                    {translate('save')}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default CompleteFamilyProfileDialog;
